import React, { lazy, Suspense, useEffect } from 'react';
import { createBrowserRouter, RouterProvider, Navigate, Outlet } from 'react-router-dom';
import { ThemeProvider as MuiThemeProvider, createTheme, StyledEngineProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { SnackbarProvider } from 'notistack';
import { NotificationProvider } from './hooks/useNotification';
import LoadingFallback from './components/LoadingFallback';
import ErrorBoundary from './components/ErrorBoundary';
import { LanguageProvider, useLanguage } from './contexts/LanguageContext';
import { ThemeProvider, useTheme } from './contexts/ThemeContext';
import { CacheProvider } from '@emotion/react';
import createCache from '@emotion/cache';
import { prefixer } from 'stylis';
import rtlPlugin from 'stylis-plugin-rtl';
import { reloadTranslations } from './i18n/i18n';

// Import i18n configuration
import './i18n/i18n';

// Lazy load mobile-specific components
const MobileLayout = lazy(() => import(/* webpackChunkName: "mobile-layout" */ './components/mobile/MobileLayout'));
const MobileDashboard = lazy(() => 
  import(/* webpackChunkName: "mobile-dashboard" *//* webpackPrefetch: true */ './components/mobile/MobileDashboard')
);
const MobileLogin = lazy(() => 
  import(/* webpackChunkName: "mobile-auth" *//* webpackPrefetch: true */ './components/mobile/MobileLogin')
);
const MobileSettings = lazy(() => import(/* webpackChunkName: "mobile-settings" */ './components/mobile/MobileSettings'));
const MobileRecycleBin = lazy(() => import(/* webpackChunkName: "mobile-recycle-bin" */ './components/mobile/MobileRecycleBin'));
const MobileAutomationRules = lazy(() => import(/* webpackChunkName: "mobile-automation-rules" */ './components/mobile/MobileAutomationRules'));
const MobileInvoiceDetails = lazy(() => import(/* webpackChunkName: "mobile-invoice-details" */ './components/mobile/MobileInvoiceDetails'));
const PrivacyPolicy = lazy(() => import(/* webpackChunkName: "privacy-policy" */ './components/PrivacyPolicy'));

// Context providers with explicit chunk names
const CurrencyProvider = lazy(() => 
  import(/* webpackChunkName: "context" */ './context/CurrencyContext')
    .then(module => ({ default: module.CurrencyProvider }))
);

// Create rtl cache
const cacheRtl = createCache({
  key: 'muirtl',
  stylisPlugins: [prefixer, rtlPlugin],
});

// Create ltr cache
const cacheLtr = createCache({
  key: 'mui',
});

const getDirection = (language) => {
  return language === 'he' ? 'rtl' : 'ltr';
};

// Light theme configuration - mobile optimized
const lightTheme = {
  palette: {
    mode: 'light',
    primary: {
      main: '#1976d2',
    },
    secondary: {
      main: '#dc004e',
    },
    background: {
      default: '#f5f5f5',
      paper: '#ffffff'
    }
  },
  typography: {
    fontSize: 14,
    h1: { fontSize: '1.6rem' },
    h2: { fontSize: '1.4rem' },
    h3: { fontSize: '1.2rem' },
    h4: { fontSize: '1.1rem' },
    h5: { fontSize: '1rem' },
    h6: { fontSize: '0.9rem' },
    body1: { fontSize: '0.9rem' },
    body2: { fontSize: '0.8rem' },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          textTransform: 'none',
          padding: '8px 16px',
        },
      },
    },
    MuiListItem: {
      styleOverrides: {
        root: {
          padding: '12px 16px',
        },
      },
    },
    MuiBottomNavigation: {
      styleOverrides: {
        root: {
          height: 64,
        },
      },
    },
    MuiBottomNavigationAction: {
      styleOverrides: {
        root: {
          padding: '8px 0',
          minWidth: 0,
        },
      },
    },
  },
};

// Dark theme configuration - mobile optimized
const darkTheme = {
  palette: {
    mode: 'dark',
    primary: {
      main: '#90caf9',
    },
    secondary: {
      main: '#f48fb1',
    },
    background: {
      default: '#121212',
      paper: '#1e1e1e'
    }
  },
  typography: {
    fontSize: 14,
    h1: { fontSize: '1.6rem' },
    h2: { fontSize: '1.4rem' },
    h3: { fontSize: '1.2rem' },
    h4: { fontSize: '1.1rem' },
    h5: { fontSize: '1rem' },
    h6: { fontSize: '0.9rem' },
    body1: { fontSize: '0.9rem' },
    body2: { fontSize: '0.8rem' },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          textTransform: 'none',
          padding: '8px 16px',
        },
      },
    },
    MuiListItem: {
      styleOverrides: {
        root: {
          padding: '12px 16px',
        },
      },
    },
    MuiBottomNavigation: {
      styleOverrides: {
        root: {
          height: 64,
        },
      },
    },
    MuiBottomNavigationAction: {
      styleOverrides: {
        root: {
          padding: '8px 0',
          minWidth: 0,
        },
      },
    },
  },
};

const createMobileRouter = () => {
  const PrivateRoute = ({ children }) => {
    const { currentUser } = useAuth();
    return currentUser ? children : <Navigate to="/login" />;
  };

  const withSuspense = (Component, loadingMessage) => (
    <Suspense fallback={<LoadingFallback message={loadingMessage} />}>
      <ErrorBoundary>
        <Component />
      </ErrorBoundary>
    </Suspense>
  );

  return createBrowserRouter([
    {
      element: <Outlet />,
      children: [
        {
          path: '/',
          element: <PrivateRoute><MobileLayout /></PrivateRoute>,
          children: [
            {
              index: true,
              element: withSuspense(MobileDashboard, 'Loading dashboard...')
            },
            {
              path: 'settings',
              element: withSuspense(MobileSettings, 'Loading settings...')
            },
            {
              path: 'recycle-bin',
              element: withSuspense(MobileRecycleBin, 'Loading recycle bin...')
            },
            {
              path: 'automation-rules',
              element: withSuspense(MobileAutomationRules, 'Loading automation rules...')
            },
            {
              path: 'invoices/:invoiceId',
              element: withSuspense(MobileInvoiceDetails, 'Loading invoice details...')
            },
          ]
        },
        {
          path: '/login',
          element: withSuspense(MobileLogin, 'Loading login page...')
        },
        {
          path: '/privacy-policy',
          element: withSuspense(PrivacyPolicy, 'Loading privacy policy...')
        }
      ]
    }
  ], {
    future: {
      v7_startTransition: true,
      v7_relativeSplatPath: true
    }
  });
};

function MobileApp() {
  // Reload translations when the app starts
  useEffect(() => {
    reloadTranslations();
  }, []);

  return (
    <ErrorBoundary>
      <LanguageProvider>
        <ThemeProvider>
          <MobileAppWithLanguageAndTheme />
        </ThemeProvider>
      </LanguageProvider>
    </ErrorBoundary>
  );
}

function MobileAppWithLanguageAndTheme() {
  const { currentLanguage } = useLanguage();
  const { mode } = useTheme();
  const direction = getDirection(currentLanguage);
  const cache = direction === 'rtl' ? cacheRtl : cacheLtr;
  
  // Create theme with direction and mode
  const theme = createTheme({
    direction: direction,
    ...(mode === 'light' ? lightTheme : darkTheme)
  });

  return (
    <CacheProvider value={cache}>
      <MuiThemeProvider theme={theme}>
        <CssBaseline />
        <AuthProvider>
          <Suspense fallback={<LoadingFallback message="Loading app..." />}>
            <SnackbarProvider 
              maxSnack={3} 
              anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
              autoHideDuration={3000}
            >
              <NotificationProvider>
                <CurrencyProvider>
                  <RouterProvider router={createMobileRouter()} />
                </CurrencyProvider>
              </NotificationProvider>
            </SnackbarProvider>
          </Suspense>
        </AuthProvider>
      </MuiThemeProvider>
    </CacheProvider>
  );
}

export default MobileApp;
